import React from 'react';

import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Mail, Printer } from 'lucide-react';
import axios from 'axios';
import { AssignedFormActions } from '../AssignedFormActions';

export const ComplianceTab = ({ complianceOnly, dashboardData, error, loadDashboard, printComplianceReport, showSmsSelectionModal, toast }) => (
  <>
    <div className="space-y-4" data-testid="compliance-tab-body">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => printComplianceReport(complianceOnly)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-md"
          data-testid="compliance-print-report-btn"
        >
          <Printer className="h-3.5 w-3.5" />
          Print Compliance Report
        </button>
      </div>
      {complianceOnly.map((consent) => (
        <div
          key={consent.id}
          className={`p-4 rounded-lg border ${
            consent.isXrayRefusalForm
              ? 'bg-orange-50 border-orange-200'
              : consent.isPerioRefusalForm
              ? 'bg-red-50 border-red-200'
              : consent.isFailureToCompleteForm
              ? 'bg-purple-50 border-purple-200'
              : consent.isPatientComplianceForm
              ? 'bg-teal-50 border-teal-200'
              : 'bg-amber-50 border-amber-200'
          }`}
          data-testid={`compliance-record-${consent.id}`}
        >
          <div className="flex justify-between items-start mb-3">
            <div className="flex-1 min-w-0">
              <p className={`font-semibold text-base ${
                consent.isXrayRefusalForm
                  ? 'text-orange-900'
                  : consent.isPerioRefusalForm
                  ? 'text-red-900'
                  : consent.isFailureToCompleteForm
                  ? 'text-purple-900'
                  : consent.isPatientComplianceForm
                  ? 'text-teal-900'
                  : 'text-amber-900'
              }`}>
                {consent.consentName}
              </p>
              <p className="text-sm text-gray-600">
                {consent.dentistName && consent.dentistName.startsWith('Dr.')
                  ? consent.dentistName
                  : `Dr. ${consent.dentistName}`}
                <span className="text-gray-400 ml-2">
                  {new Date(consent.assignedDate).toLocaleDateString()}
                </span>
              </p>
              <p className="text-xs text-gray-500">Patient: {consent.patientName || ''}</p>
              {consent.isRefusalForm && consent.treatmentRefused && (
                <p className="text-xs text-amber-800 mt-1">
                  <strong>Treatment refused:</strong> {consent.treatmentRefused}
                </p>
              )}
              {consent.isXrayRefusalForm && (
                <p className="text-xs text-orange-800 mt-1">
                  <strong>Type:</strong> X-Ray Refusal Acknowledgment
                </p>
              )}
              {consent.isPerioRefusalForm && (
                <p className="text-xs text-red-800 mt-1">
                  <strong>Type:</strong> Periodontal Refusal of Treatment
                </p>
              )}
              {consent.isFailureToCompleteForm && (
                <p className="text-xs text-purple-800 mt-1">
                  <strong>Type:</strong> Failure to Complete Treatment
                </p>
              )}
              {consent.isPatientComplianceForm && (
                <p className="text-xs text-teal-800 mt-1">
                  <strong>Type:</strong> Patient Compliance Form
                </p>
              )}
              {consent.lastResentAt && (
                <p className="text-[11px] text-gray-500 mt-1">
                  Last resent: {new Date(consent.lastResentAt).toLocaleString()}
                  {consent.resendCount > 1 ? ` (${consent.resendCount} times)` : ''}
                </p>
              )}
            </div>
            <div className="flex flex-col items-end gap-1.5 ml-3 flex-shrink-0">
              {(consent.status === 'signed' || consent.status === 'signed_in_office') ? (
                <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
                  {consent.status === 'signed_in_office' ? 'Signed (In-Office)' : 'Signed'}
                  {consent.signedAt ? ` ${new Date(consent.signedAt).toLocaleDateString()}` : ''}
                </Badge>
              ) : (
                <>
                  <Badge
                    variant="outline"
                    className="bg-red-600 text-white border-red-700 font-bold tracking-wide uppercase text-[11px] px-2.5 py-0.5"
                    data-testid={`compliance-unsigned-badge-${consent.id}`}
                  >
                    Unsigned
                  </Badge>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs px-2"
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        const resp = await axios.post(
                          `${process.env.REACT_APP_BACKEND_URL}/api/informed-consent/resend-signing-link/${consent.id}`,
                          {},
                          { headers: { Authorization: `Bearer ${localStorage.getItem('dentalToken')}` } }
                        );
                        const d = resp.data || {};
                        const channels = [];
                        if (d.emailSent) channels.push(`email (${d.patientEmail})`);
                        if (d.smsSent) channels.push(`SMS (${d.patientPhone})`);
                        toast({
                          title: 'Signing link resent',
                          description: channels.length
                            ? `Sent via ${channels.join(' + ')}`
                            : 'Resent — but no working email or phone on file.',
                        });
                        // Refresh dashboard so the "Last resent" timestamp shows up
                        if (typeof loadDashboard === 'function') loadDashboard(true);
                      } catch (err) {
                        toast({
                          title: 'Could not resend',
                          description: err.response?.data?.detail || 'Unknown error',
                          variant: 'destructive',
                        });
                      }
                    }}
                    data-testid={`compliance-resend-btn-${consent.id}`}
                  >
                    <Mail className="h-3 w-3 mr-1" />
                    Resend Link
                  </Button>
                </>
              )}
            </div>
          </div>
          <AssignedFormActions
            consent={consent}
            patient={dashboardData?.recentPatients?.find((p) => p.id === consent.patientId)}
            onRefresh={() => loadDashboard(true)}
            onText={() => {
              const patient = dashboardData?.recentPatients?.find((p) => p.id === consent.patientId);
              showSmsSelectionModal(
                consent.patientId,
                'consent',
                consent.id,
                patient?.cellPhone || patient?.phone,
                `${patient?.firstName || ''} ${patient?.lastName || ''}`.trim(),
                consent.consentName
              );
            }}
          />
        </div>
      ))}
    </div>
  </>
);
